import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  UseFormRegister,
  FieldErrors,
  Control,
  useWatch,
  Controller,
} from "react-hook-form";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import CustomButton from "../../../components/AddButton";
import FormTitle from "../../../components/FormTitle";
import CustomModal from "../../../components/Modal";
import { MASTER_URL } from "../../../common/common";
import DatePicker from "../../../components/Datepicker";
import { ApiService } from "../../../service/api.service";
import CustomDropDown from "../../../components/Dropdown/CustomDropDown";
import MapComponent from "../../../components/AuthMap";
import DropDownMap from "../../../components/AuthMap/DropDownMap";
import { RouteFormData, RouteOption, RoutesProps } from "../../../service/interfaces/ApiResponse.interface";

const Routes: React.FC<RoutesProps> = ({
  errors,
  register,
  setValue,
  control,
  setRouteDetailsData,
}) => {
  const apiService = new ApiService();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState<RouteOption[]>([]);
  const [modes, setModes] = useState<RouteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasFetched = useRef(false);
  const watchFields = useWatch({ control }); // Watch all form fields

  // Helper function to get field error message
  const getFieldError = (fieldName: string): string => {
    return (errors as any)?.[fieldName]?.message || "";
  };

  // Helper function to check if field has error
  const hasFieldError = (fieldName: string): boolean => {
    return !!(errors as any)?.[fieldName];
  };

  // Memoized function to parse location data
  const parseLocationData = useCallback((locationData: any) => {
    if (!locationData) return { point: "", lat: null, lng: null };

    try {
      // If it's a JSON string, parse it
      if (
        typeof locationData === "string" &&
        (locationData.startsWith("{") || locationData.includes('"value"'))
      ) {
        const parsed = JSON.parse(locationData);
        return {
          point: parsed.address || parsed.value || "",
          lat: parsed.lat || null,
          lng: parsed.lng || null,
        };
      }
      // If it's already an object
      else if (typeof locationData === "object" && locationData !== null) {
        return {
          point: locationData.address || locationData.value || "",
          lat: locationData.lat || null,
          lng: locationData.lng || null,
        };
      }
      // If it's just a string
      return {
        point: locationData,
        lat: null,
        lng: null,
      };
    } catch (e) {
      // If parsing fails, just use the string value
      return {
        point: typeof locationData === "string" ? locationData : "",
        lat: null,
        lng: null,
      };
    }
  }, []);

  const fetchRouteData = async (): Promise<void> => {
    if (hasFetched.current || isLoading) {
      return;
    }

    try {
      hasFetched.current = true;
      setIsLoading(true);
      // Fetch routes from the master data service
      const response: any = await apiService.getApi(
        `${MASTER_URL}/route/get-route`
      );

      if (response.status === "200" || response.status === 200) {
        // Check if response.body.data exists (new structure) or response.body is array (old structure)
        const routesData = response.body.data || response.body;
        if (routesData && Array.isArray(routesData)) {
          const formattedOptions = routesData.map((route: any) => ({
            value: route.routeId,
            label: route.name,
            routeData: route // Store full route data for auto-fill
          }));
          setRoutes(formattedOptions);
        }
      }

      // Keep Route Mode as it was previously - fetch from booking data (same as main form)
      const modesResponse: any = await apiService.getApi(
        `${MASTER_URL}/booking/data-route?key=Booking Type`
      );


      if (modesResponse.status === "200" || modesResponse.status === 200) {
        if (modesResponse.body && modesResponse.body["Movement Mode"]) {
          const formattedModes = modesResponse.body["Movement Mode"].map((item: any) => ({
            value: item.metaId,
            label: item.value,
          }));
          setModes(formattedModes);
        } else if (modesResponse.body && modesResponse.body["RouteMode"]) {
          // Check if RouteMode exists in the response
          const formattedModes = modesResponse.body["RouteMode"].map((item: any) => ({
            value: item.metaId,
            label: item.value,
          }));
          setModes(formattedModes);
        } else if (modesResponse.body && modesResponse.body["Route"]) {
          // Check if Route contains route mode data
          const formattedModes = modesResponse.body["Route"].map((item: any) => ({
            value: item.metaId,
            label: item.value,
          }));
          setModes(formattedModes);
        } else {
          // Route Mode data not available - set empty array
          setModes([]);
        }
      }
    } catch (error) {
      console.error("Error fetching route data:", error);
      // Set empty arrays in case of error
      setRoutes([]);
      setModes([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Effect to fetch routes on component mount
  useEffect(() => {
    fetchRouteData();
  }, []);

  // Effect to collect and update route details data
  useEffect(() => {
    if (!watchFields) return;

    // Extract the location data properly
    const pickupPointData = watchFields.pickupPoint || "";
    const loadingPointData = watchFields.loadingPoint || "";
    const clearancePointData = watchFields.clearancePoint || "";
    const unloadingPointData = watchFields.unloadingPoint || "";

    // Parse location data for each point
    const pickupData = parseLocationData(pickupPointData);
    const loadingData = parseLocationData(loadingPointData);
    const clearanceData = parseLocationData(clearancePointData);
    const unloadingData = parseLocationData(unloadingPointData);

    // Create the data object with all fields
    const routeFormData: RouteFormData = {
      routeId: watchFields.routeId || "",
      routeMode: watchFields.routeMode || "",
      pickupPoint: pickupData.point,
      pickupAt: watchFields.pickupDate || "",
      pickupLatitude: pickupData.lat,
      pickupLongitude: pickupData.lng,
      loadingPoint: loadingData.point,
      loadingAt: watchFields.loadingDate || "",
      loadingLatitude: loadingData.lat,
      loadingLongitude: loadingData.lng,
      clearancePoint: clearanceData.point,
      clearanceAt: watchFields.clearanceDate || "",
      clearanceLatitude: clearanceData.lat,
      clearanceLongitude: clearanceData.lng,
      unloadingPoint: unloadingData.point,
      unloadingAt: watchFields.unloadingDate || "",
      unloadingLatitude: unloadingData.lat,
      unloadingLongitude: unloadingData.lng,
      distanceKm: 0,
      estimatedDuration: 0,
    };

    // Check if the form has at least one required field filled
    const hasRequiredData =
      routeFormData.routeId ||
      routeFormData.pickupPoint ||
      routeFormData.loadingPoint ||
      routeFormData.clearancePoint ||
      routeFormData.unloadingPoint;

    // Update the parent component with route details data
    if (hasRequiredData) {
      setRouteDetailsData([routeFormData]); // Pass single item in array
    } else {
      setRouteDetailsData([]); // Pass empty array if no data
    }
  }, [watchFields, setRouteDetailsData, parseLocationData]);

  // Function to handle date change with preventing automatic scrolling
  const handleDateChange = useCallback((
    date: string,
    fieldName: string
  ): void => {
    // Use requestAnimationFrame to ensure the setValue happens after the current render
    requestAnimationFrame(() => {
      setValue(fieldName, date, {
        shouldValidate: true,
        shouldDirty: true
      });
    });
  }, [setValue]);

  // Handle the "Same as loading point" checkbox
  const handleSameAsLoading = useCallback((
    e: React.ChangeEvent<HTMLInputElement>,
    targetField: string
  ): void => {
    const isChecked = e.target.checked;

    if (isChecked) {
      const loadingPoint = watchFields?.loadingPoint || "";
      setValue(targetField, loadingPoint, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  }, [watchFields, setValue]);

  // Handle the "Enter Manually" action
  const handleEnterManually = useCallback((fieldName: string): void => {
    // This could show a modal or enable direct editing
    // For now, it just clears the field to allow manual entry
    setValue(fieldName, "", {
      shouldValidate: true,
      shouldDirty: true
    });
    // Focus on the field (would require ref handling for complete implementation)
  }, [setValue]);

  // Handle route selection and auto-fill form fields
  const handleRouteSelection = useCallback((selectedRouteId: string | number) => {
    const selectedRoute = routes.find(route => route.value === selectedRouteId);
    if (selectedRoute && selectedRoute.routeData) {
      const routeData = selectedRoute.routeData;

      // Auto-fill pickup point
      if (routeData.pickupPoint) {
        setValue("pickupPoint", {
          address: routeData.pickupPoint,
          lat: routeData.pickupLatitude,
          lng: routeData.pickupLongitude
        }, { shouldValidate: true, shouldDirty: true });
      }

      // Auto-fill loading point
      if (routeData.loadingPoint) {
        setValue("loadingPoint", {
          address: routeData.loadingPoint,
          lat: routeData.loadingLatitude,
          lng: routeData.loadingLongitude
        }, { shouldValidate: true, shouldDirty: true });
      }

      // Auto-fill clearance point
      if (routeData.clearancePoint) {
        setValue("clearancePoint", {
          address: routeData.clearancePoint,
          lat: routeData.clearanceLatitude,
          lng: routeData.clearanceLongitude
        }, { shouldValidate: true, shouldDirty: true });
      }

      // Auto-fill unloading point
      if (routeData.unloadingPoint) {
        setValue("unloadingPoint", {
          address: routeData.unloadingPoint,
          lat: routeData.unloadingLatitude,
          lng: routeData.unloadingLongitude
        }, { shouldValidate: true, shouldDirty: true });
      }
    }
  }, [routes, setValue]);

  // Handle the "Create New Route" action
  const handleCreateNewRoute = (): void => {
    // Navigate to routes add page with return URL
    const returnUrl = encodeURIComponent('/booking/newbooking');
    navigate(`/routes/add?returnUrl=${returnUrl}`);
  };

  // Handle the "Save as Template" action
  const handleSaveAsTemplate = async (): Promise<void> => {
    try {
      // Get current form values
      const formData = watchFields;

      // Check if user has entered new values (not using existing route data)
      const hasNewValues = checkForNewValues(formData);

      if (!hasNewValues) {
        toast.error("Please enter new route data before saving as template. You are currently using existing route data.");
        return;
      }

      // Validate required fields
      if (!formData.pickupPoint?.address || !formData.loadingPoint?.address ||
        !formData.clearancePoint?.address || !formData.unloadingPoint?.address) {
        toast.error("Please fill in all required route points before saving as template.");
        return;
      }

      // Prepare route data for API
      const routeData = {
        name: `Template - ${new Date().toLocaleString()}`,
        pickupPoint: formData.pickupPoint?.address || '',
        pickupLatitude: formData.pickupPoint?.lat?.toString() || '0',
        pickupLongitude: formData.pickupPoint?.lng?.toString() || '0',
        loadingPoint: formData.loadingPoint?.address || '',
        loadingLatitude: formData.loadingPoint?.lat?.toString() || '0',
        loadingLongitude: formData.loadingPoint?.lng?.toString() || '0',
        clearancePoint: formData.clearancePoint?.address || '',
        clearanceLatitude: formData.clearancePoint?.lat?.toString() || '0',
        clearanceLongitude: formData.clearancePoint?.lng?.toString() || '0',
        unloadingPoint: formData.unloadingPoint?.address || '',
        unloadingLatitude: formData.unloadingPoint?.lat?.toString() || '0',
        unloadingLongitude: formData.unloadingPoint?.lng?.toString() || '0',
        isActive: true
      };

      // Call the add route API
      const response: any = await apiService.postApi(`${MASTER_URL}/route/store-route`, routeData);

      if (response && response.status === "200") {
        toast.success("Route template saved successfully!");
        // Optionally refresh the routes list
        fetchRouteData();
      } else {
        toast.error("Failed to save route template. Please try again.");
      }
    } catch (error) {
      console.error("Error saving route template:", error);
      toast.error("Error saving route template. Please try again.");
    }
  };

  // Check if user has entered new values (not using existing route data)
  const checkForNewValues = (formData: any): boolean => {
    // If no route is selected, user is entering new data
    if (!formData.routeId) {
      return true;
    }

    // If route is selected, check if any field has been modified from the original route data
    const selectedRoute = routes.find(route => route.value === formData.routeId);
    if (!selectedRoute?.routeData) {
      return true; // No original data to compare against
    }

    const originalRoute = selectedRoute.routeData;

    // Check if any point has been modified
    const pickupModified = formData.pickupPoint?.address !== originalRoute.pickupPoint;
    const loadingModified = formData.loadingPoint?.address !== originalRoute.loadingPoint;
    const clearanceModified = formData.clearancePoint?.address !== originalRoute.clearancePoint;
    const unloadingModified = formData.unloadingPoint?.address !== originalRoute.unloadingPoint;

    return pickupModified || loadingModified || clearanceModified || unloadingModified;
  };

  return (
    <div className="bg-white p-4 rounded-[20px]">
      <div className="flex items-center justify-between mb-4">
        <FormTitle title="Route Details" />
      </div>

      <div className="overflow-x-auto">
        <div className="mb-6 border-b pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <div className="w-full">
              <Controller
                name="routeId"
                control={control}
                render={({ field }) => (
                  <CustomDropDown
                    errors={
                      hasFieldError("routeId")
                        ? {
                          routeId: {
                            message: getFieldError("routeId"),
                          },
                        }
                        : {}
                    }
                    register={register}
                    label="Route"
                    name="routeId"
                    options={routes}
                    defaultValue={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      handleRouteSelection(value);
                    }}
                  />
                )}
              />
              <button
                type="button"
                className="float-right text-[#444FBD] font-[400] text-[14px] mt-1"
                onClick={handleCreateNewRoute}
              >
                Create New Route
              </button>
            </div>
            <div className="w-full">
              <Controller
                name="routeMode"
                control={control}
                render={({ field }) => (
                  <CustomDropDown
                    errors={
                      hasFieldError("routeMode")
                        ? {
                          routeMode: {
                            message: getFieldError("routeMode"),
                          },
                        }
                        : {}
                    }
                    register={register}
                    label="Route Mode"
                    name="routeMode"
                    options={modes}
                    defaultValue={field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="w-full relative">
                  <Controller
                    name="pickupPoint"
                    control={control}
                    render={({ field }) => (
                      <DropDownMap
                        errors={
                          hasFieldError("pickupPoint")
                            ? {
                              pickupPoint: {
                                message: getFieldError("pickupPoint"),
                              },
                            }
                            : {}
                        }
                        register={register}
                        label="Pickup Point *"
                        name="pickupPoint"
                        setValue={setValue}
                        defaultValue={field.value}
                      />
                    )}
                  />

                  <div className="flex justify-between items-center mt-1">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-[14px] h-[14px]"
                        onChange={(e) =>
                          handleSameAsLoading(e, "pickupPoint")
                        }
                      />
                      <span className="text-[#282828] text-[14px] font-normal">
                        Same as loading point
                      </span>
                    </label>
                  </div>
                </div>

                <div className="w-full">
                  <Controller
                    name="pickupDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Date & Time"
                        onDateChange={(date: string) =>
                          handleDateChange(date, "pickupDate")
                        }
                        register={register("pickupDate")}
                        name="pickupDate"
                        disabled={false}
                        defaultValue={field.value}
                        includeTime={true}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="w-full relative">
                  <Controller
                    name="loadingPoint"
                    control={control}
                    render={({ field }) => (
                      <DropDownMap
                        errors={
                          hasFieldError("loadingPoint")
                            ? {
                              loadingPoint: {
                                message: getFieldError("loadingPoint"),
                              },
                            }
                            : {}
                        }
                        register={register}
                        label="Loading Point *"
                        name="loadingPoint"
                        setValue={setValue}
                        defaultValue={field.value}
                      />
                    )}
                  />
                </div>

                <div className="w-full">
                  <Controller
                    name="loadingDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Date & Time"
                        onDateChange={(date: string) =>
                          handleDateChange(date, "loadingDate")
                        }
                        register={register("loadingDate")}
                        name="loadingDate"
                        disabled={false}
                        defaultValue={field.value}
                        includeTime={true}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="w-full relative">
                  <Controller
                    name="clearancePoint"
                    control={control}
                    render={({ field }) => (
                      <DropDownMap
                        errors={
                          hasFieldError("clearancePoint")
                            ? {
                              clearancePoint: {
                                message: getFieldError("clearancePoint"),
                              },
                            }
                            : {}
                        }
                        register={register}
                        label="Clearance Point *"
                        name="clearancePoint"
                        setValue={setValue}
                        defaultValue={field.value}
                      />
                    )}
                  />

                  <div className="flex justify-between items-center mt-1">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-[14px] h-[14px]"
                        onChange={(e) =>
                          handleSameAsLoading(e, "clearancePoint")
                        }
                      />
                      <span className="text-[#282828] text-[14px] font-normal">
                        Same as loading point
                      </span>
                    </label>
                  </div>
                </div>

                <div className="w-full">
                  <Controller
                    name="clearanceDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Date & Time"
                        onDateChange={(date: string) =>
                          handleDateChange(date, "clearanceDate")
                        }
                        register={register("clearanceDate")}
                        name="clearanceDate"
                        disabled={false}
                        defaultValue={field.value}
                        includeTime={true}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="w-full relative">
                  <Controller
                    name="unloadingPoint"
                    control={control}
                    render={({ field }) => (
                      <DropDownMap
                        errors={
                          hasFieldError("unloadingPoint")
                            ? {
                              unloadingPoint: {
                                message: getFieldError("unloadingPoint"),
                              },
                            }
                            : {}
                        }
                        register={register}
                        label="UnLoading Point *"
                        name="unloadingPoint"
                        setValue={setValue}
                        defaultValue={field.value}
                      />
                    )}
                  />

                  <div className="flex justify-between items-center mt-1">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-[14px] h-[14px]"
                        onChange={(e) =>
                          handleSameAsLoading(e, "unloadingPoint")
                        }
                      />
                      <span className="text-[#282828] text-[14px] font-normal">
                        Same as loading point
                      </span>
                    </label>
                  </div>
                </div>

                <div className="w-full">
                  <Controller
                    name="unloadingDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Date & Time"
                        onDateChange={(date: string) =>
                          handleDateChange(date, "unloadingDate")
                        }
                        register={register("unloadingDate")}
                        name="unloadingDate"
                        disabled={false}
                        defaultValue={field.value}
                        includeTime={true}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="w-full md:w-1/3 lg:w-2/5 mt-6">
              <MapComponent height="350px" />
            </div>
          </div>
          <button
            type="button"
            onClick={handleSaveAsTemplate}
            className="float-right bg-[#444FBD] hover:bg-blue-600 rounded-[5px] h-[40px] w-[180px] text-white transition-colors"
          >
            save as template
          </button>
        </div>
      </div>
    </div>
  );
};

export default Routes;